/**
 * Redux action + middleware that owns the Grahak auth-expired side-effect.
 *
 * Replaces the previous `window.dispatchEvent(new CustomEvent('auth:expired'))`
 * + `window.addEventListener('auth:expired', ...)` pair that lived in
 * `ApiHelper` and `AppContainer`. Centralizing the side-effect here:
 *
 *   1. Keeps the React tree free of imperative auth-recovery wiring.
 *   2. Makes the handler trivially unit-testable by dispatching the action
 *      against a store with this middleware attached.
 *   3. Removes the `CustomEvent` global hop, which was awkward to mock and
 *      could leak across pages if listeners weren't cleaned up.
 *
 * Circuit-breaker (per-tab):
 *   - We allow the recovery sequence to run AT MOST `MAX_RECOVERIES_PER_TAB`
 *     times. The counter is stored in sessionStorage for the tab lifetime.
 *   - If guest re-init fails OR the counter is exceeded, we bail out:
 *     clear all auth storage, mark Redux as guest, and redirect only when the
 *     current route is protected.
 *     This prevents the "reload loop" pathological case where the backend
 *     is down and every reload fires another `auth:expired`.
 */

import type { Dispatch, MiddlewareAPI } from 'redux';
import {
  blockRecovery,
  finishAuthRecovery,
  getRecoveryCount,
  incrementRecoveryCount,
  isRecoveryBlocked,
  resetAuthRecoveryState,
  startAuthRecovery,
} from 'shared/helpers/api/authRecoveryState';
import { isEligibleforGrahakRuleFromState } from 'shared/api/grahakAuthSurface';
import { getAuthAPI } from 'shared/api/authResolverLoader';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';
import { trackGrahakFunctionality } from 'shared/helpers/grahakMonitoring';
import { shouldRedirectHomeAfterAuthRecovery } from './routePolicy';

// NOTE: All side-effect imports below are LAZY (`require(...)` inside the
// handler) on purpose. The static authRecoveryState import above is a pure
// sessionStorage adapter and does not enter the auth/store dependency graph.
// Static side-effect imports here cause a circular dependency:
//
//   shared/store -> shared/store/auth/middleware -> shared/api/authResolver
//     -> shared/api/cart/helpers -> shared/store   (getStore not yet exported)
//
// The middleware only ever runs in the browser at action-dispatch time, by
// which point every module in the graph has finished evaluating, so lazy
// requires are safe and break the cycle cleanly.

export const AUTH_EXPIRED = 'auth/expired';

export const authExpired = () => ({ type: AUTH_EXPIRED } as const);

const DEFAULT_MAX_RECOVERIES_PER_TAB = 2;
const MAX_RECOVERIES_PER_TAB = 5;

type TabState = {
  isRecovering: boolean;
};

const getTabState = (): TabState => {
  if (typeof window === 'undefined') {
    return { isRecovering: false };
  }
  const w = window as Window & { __grahakAuthExpiredState?: TabState };
  if (!w.__grahakAuthExpiredState) {
    w.__grahakAuthExpiredState = { isRecovering: false };
  }
  return w.__grahakAuthExpiredState;
};

const getMaxRecoveries = (store?: MiddlewareAPI): number => {
  const state = store?.getState?.();
  const { maxRecoveryAttempts } = (state && getRemoteConfig(state, 'grahakAuth')) || {};
  const value = Number(maxRecoveryAttempts);

  if (!Number.isInteger(value) || value <= 0) {
    return DEFAULT_MAX_RECOVERIES_PER_TAB;
  }

  return Math.min(value, MAX_RECOVERIES_PER_TAB);
};

const safeRedirectHome = (): void => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/') return;

  if (typeof window.location.assign === 'function') {
    window.location.assign('/');
    return;
  }

  window.location.href = '/';
};

const clearAllAuthArtifacts = (): void => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { clearStoredUserFormKey } = require('shared/helpers/api/authFormKeyStorage');
    clearStoredUserFormKey();
  } catch (e) {
    // ignore
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const storage = require('shared/helpers/storage').default;
    storage.clear();
  } catch (e) {
    // ignore
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { clearFreeGiftsSessionKey } = require('shared/components/CartCTA/helpers');
    clearFreeGiftsSessionKey();
  } catch (e) {
    // ignore
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { clearGuestInitialized } = require('shared/api/grahakSession');
    clearGuestInitialized();
  } catch (e) {
    // ignore
  }
};

const markStoreAsGuest = (store?: MiddlewareAPI): void => {
  if (!store?.dispatch) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { setGuestSession } = require('shared/store/app/actions');
    store.dispatch(setGuestSession());
  } catch (e) {
    // Store may not be ready during very early boot.
    void e;
  }
};

const refreshGuestUser = async (store?: MiddlewareAPI): Promise<void> => {
  if (!store?.dispatch) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { fetchUser } = require('shared/store/app/fetchUser');
    await store.dispatch(fetchUser());
  } catch (e) {
    // Guest identity is already reflected in Redux; keep the cleared count if
    // the authoritative guest user/cart refresh is unavailable.
    void e;
  }
};

const shouldRedirectCurrentRouteHome = (store?: MiddlewareAPI): boolean =>
  typeof window !== 'undefined' &&
  shouldRedirectHomeAfterAuthRecovery(
    window.location.pathname,
    store?.getState?.()?.app?.storeId
  );

const runRecovery = async (store?: MiddlewareAPI): Promise<void> => {
  const state = getTabState();

  if (state.isRecovering || isRecoveryBlocked()) return;
  state.isRecovering = true;
  const recoveryCount = getRecoveryCount();
  const shouldRedirectHome = shouldRedirectCurrentRouteHome(store);

  // Circuit breaker — if we've already recovered the allowed number of times
  // in this tab, do not attempt logout / guest init again. Clear authenticated
  // state and discard a protected component tree, while public routes stay put.
  if (recoveryCount >= getMaxRecoveries(store)) {
    trackGrahakFunctionality({
      functionality: 'auth_recovery',
      outcome: 'failure',
      reason: 'max_attempts_exceeded',
      attempt: recoveryCount,
    });
    blockRecovery();
    clearAllAuthArtifacts();
    markStoreAsGuest(store);
    if (shouldRedirectHome) {
      safeRedirectHome();
    }
    state.isRecovering = false;
    return;
  }

  incrementRecoveryCount();
  trackGrahakFunctionality({
    functionality: 'auth_recovery',
    outcome: 'started',
    reason: 'session_expired',
    attempt: recoveryCount + 1,
  });
  startAuthRecovery();

  try {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const { logoutUser } = require('shared/api/auth');
      await logoutUser();
    } catch (err) {
      // Logout API failure is non-fatal; we still clear local state.
      trackGrahakFunctionality({
        functionality: 'logout_during_auth_recovery',
        outcome: 'failure',
        reason: 'request_failed',
        error: err,
        attempt: recoveryCount + 1,
      });
    }

    clearAllAuthArtifacts();

    try {
      const AuthAPI = await getAuthAPI();
      const result = await AuthAPI.initGuestSession({ forceGuest: true });
      if (result?.status === 'skipped') {
        trackGrahakFunctionality({
          functionality: 'auth_recovery',
          outcome: 'failure',
          reason: 'grahak_disabled_during_recovery',
          attempt: recoveryCount + 1,
        });
        markStoreAsGuest(store);
        if (shouldRedirectHome) {
          safeRedirectHome();
        }
        return;
      }
    } catch (err) {
      // Keep subsequent 401s terminal until a verified login resets recovery.
      trackGrahakFunctionality({
        functionality: 'auth_recovery',
        outcome: 'failure',
        reason: 'guest_session_failed',
        error: err,
        attempt: recoveryCount + 1,
      });
      blockRecovery();
      markStoreAsGuest(store);
      if (shouldRedirectHome) {
        safeRedirectHome();
      }
      return;
    }

    // Use the middleware's live store after guest initialization succeeds.
    // Clear authenticated UI immediately. Every non-protected route remains
    // mounted by default and receives the authoritative guest /user state.
    markStoreAsGuest(store);
    if (!shouldRedirectHome) {
      await refreshGuestUser(store);
    }

    // Protected routes need a hard navigation so their authenticated component
    // tree is discarded. New and unknown routes stay mounted by default.
    trackGrahakFunctionality({
      functionality: 'auth_recovery',
      outcome: 'success',
      reason: 'guest_session_restored',
      attempt: recoveryCount + 1,
    });
    if (typeof window !== 'undefined' && shouldRedirectHome) {
      safeRedirectHome();
    }
  } finally {
    finishAuthRecovery();
    state.isRecovering = false;
  }
};

/**
 * Reset per-tab recovery state. Exported for deterministic tests.
 */
export const __resetAuthExpiredTabState = (): void => {
  if (typeof window === 'undefined') return;
  const w = window as Window & { __grahakAuthExpiredState?: TabState };
  w.__grahakAuthExpiredState = { isRecovering: false };
  resetAuthRecoveryState();
};

export const authMiddleware =
  (_store: MiddlewareAPI) =>
  (next: Dispatch) =>
  (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any
  ) => {
    if (action?.type === AUTH_EXPIRED) {
      // Legacy users (non-Grahak) never enter this flow; the resolver always
      // returns false and `ApiHelper` never dispatches `AUTH_EXPIRED`. Belt
      // and braces — bail here too in case a stray dispatch shows up.
      const eligible = isEligibleforGrahakRuleFromState(_store?.getState?.());

      if (!eligible) {
        return next(action);
      }

      // Fire-and-forget. We still pass the action down so reducers/devtools
      // can observe it. The recovery itself runs async.
      void runRecovery(_store);
    }

    return next(action);
  };
